import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { UrgentRequestBanner } from "@/components/UrgentRequestBanner";
import { FollowUpBanner } from "@/components/FollowUpBanner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, Wallet, Coins, Trophy, Building2, PiggyBank, ShoppingCart, Ship, Users, Factory, Landmark, AlertTriangle, Quote, ChevronLeft, ChevronRight, ThumbsUp, Heart, Target, FileText, Wrench } from "lucide-react";

const MOTIVATIONAL_QUOTES = [
  "Great things in business are never done by one person; they're done by a team.",
  "Opportunities don't happen. You create them.",
  "Success is where preparation and opportunity meet.",
  "The secret of getting ahead is getting started.",
  "Quality means doing it right when no one is looking.",
  "Don't watch the clock; do what it does — keep going.",
  "The way to get started is to quit talking and begin doing.",
  "Price is what you pay. Value is what you get.",
  "Whatever you are, be a good one.",
  "The best way to predict the future is to create it.",
  "Do what you do so well that people can't resist coming back.",
  "Excellence is not a skill, it's an attitude.",
  "A goal without a plan is just a wish.",
  "Move fast and build things that last.",
  "Hard work beats talent when talent doesn't work hard.",
  "Make each day your masterpiece.",
  "Success usually comes to those too busy to be looking for it.",
  "The harder you work, the luckier you get.",
  "Small deeds done are better than great deeds planned.",
  "Discipline is the bridge between goals and accomplishment.",
  "Every expert was once a beginner.",
  "Focus on being productive instead of busy.",
  "Progress, not perfection.",
  "Take care of your customers and they'll take care of you.",
  "The best investment you can make is in yourself.",
  "Dream big. Start small. Act now.",
  "Consistency is what transforms average into excellence.",
  "Deliver more than you promise.",
  "A river cuts through rock not because of its power, but its persistence.",
  "Today's efforts are tomorrow's results.",
  "Turn every challenge into an opportunity.",
  "Winners focus on winning; losers focus on winners.",
];

function dailyQuote() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length];
}

const money = (v, ccy) => {
  if (v == null) return "—";
  const n = Number(v);
  const s = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const body = ccy === "USD" ? `$ ${s}` : `TZS ${s}`;
  return n < 0 ? `(${body})` : body;
};

function CcyToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid={testid}>
      {["TZS", "USD"].map((c) => (
        <button key={c} onClick={() => onChange(c)} className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${value === c ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`${testid}-${c}`}>{c}</button>
      ))}
    </div>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`font-heading text-sm font-bold ${tone || "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function RankCard({ icon: Icon, title, testid, rows, ccy, onCcy, conv, to }) {
  const nav = useNavigate();
  return (
    <div className="rounded-xl border border-border bg-white" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#A87F31]" />
          <h2 className="font-heading text-sm font-bold tracking-tight">{title}</h2>
        </div>
        <CcyToggle value={ccy} onChange={onCcy} testid={`${testid}-ccy`} />
      </div>
      {rows.length ? (
        <ul className="divide-y divide-border">
          {rows.map((r, i) => {
            const href = to ? to(r) : null;
            return (
              <li key={i} onClick={href ? () => nav(href) : undefined}
                className={`flex items-center justify-between px-5 py-2.5 ${href ? "cursor-pointer transition-colors hover:bg-slate-50" : ""}`}
                data-testid={`${testid}-row-${i}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">{i + 1}</span>
                  <span className={`truncate text-sm ${href ? "font-medium text-[#1B4383] hover:underline" : "text-slate-700"}`}>{r.name}</span>
                </div>
                <span className="shrink-0 font-mono text-sm font-semibold text-slate-900">{money(conv(r.value_tzs, ccy), ccy)}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">No data yet.</div>
      )}
    </div>
  );
}

function POChart({ data, rate }) {
  const [ccy, setCcy] = useState("TZS");
  const [metric, setMetric] = useState("value");
  const WINDOW = 6;
  const [end, setEnd] = useState(data.length);
  useEffect(() => { setEnd(data.length); }, [data.length]);
  const start = Math.max(0, end - WINDOW);
  const view = data.slice(start, end);
  const conv = (tzs) => (ccy === "USD" ? Number(tzs || 0) / (rate || 1) : Number(tzs || 0));
  const vals = view.map((d) => (metric === "count" ? d.count : conv(d.value_tzs)));
  const max = Math.max(1, ...vals);
  const hasData = data.some((d) => d.count > 0);
  const canPrev = start > 0;
  const canNext = end < data.length;
  return (
    <div className="rounded-xl border border-border bg-white lg:col-span-2" data-testid="po-chart-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-[#8A5A2B]" /><h2 className="font-heading text-base font-bold tracking-tight">Purchase Orders by Month</h2></div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <button onClick={() => setEnd((e) => Math.max(WINDOW, e - 1))} disabled={!canPrev} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Older months" data-testid="po-prev-month"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setEnd((e) => Math.min(data.length, e + 1))} disabled={!canNext} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Newer months" data-testid="po-next-month"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {[["value", "Value"], ["count", "Count"]].map(([k, l]) => (
              <button key={k} onClick={() => setMetric(k)} className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${metric === k ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`po-metric-${k}`}>{l}</button>
            ))}
          </div>
          {metric === "value" && <CcyToggle value={ccy} onChange={setCcy} testid="po-chart-ccy" />}
        </div>
      </div>
      <div className="px-5 py-5">
        {hasData ? (
          <div className="flex items-end justify-between gap-3" style={{ height: 190 }}>
            {view.map((d, i) => {
              const val = metric === "count" ? d.count : conv(d.value_tzs);
              const h = Math.round((val / max) * 150);
              return (
                <div key={i} className="group flex flex-1 flex-col items-center justify-end gap-1.5" data-testid={`po-bar-${d.month}`}>
                  <div className="whitespace-nowrap text-[10px] font-semibold text-slate-600 opacity-0 transition-opacity group-hover:opacity-100">{metric === "count" ? d.count : money(val, ccy)}</div>
                  <div className="w-full max-w-[46px] rounded-t bg-[#1B4383] transition-all group-hover:bg-[#8A5A2B]" style={{ height: `${val > 0 ? Math.max(h, 4) : 0}px` }} />
                  <div className="text-[11px] font-medium text-slate-500">{d.label}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-14 text-center text-sm text-muted-foreground">No purchase orders recorded yet.</div>
        )}
      </div>
    </div>
  );
}

function CashCard({ mgmt }) {
  const [ccy, setCcy] = useState("TZS");
  const [view, setView] = useState("consolidated");
  const lines = mgmt?.cash?.lines || [];
  const total = ccy === "USD" ? mgmt?.cash?.total_usd : mgmt?.cash?.total_tzs;
  return (
    <div className="rounded-xl border border-border bg-white" data-testid="cash-breakdown">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-4">
        <div className="flex items-center gap-2"><PiggyBank className="h-4 w-4 text-[#A87F31]" /><h2 className="font-heading text-base font-bold tracking-tight">Cash in Bank</h2></div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {[["consolidated", "Summary"], ["detailed", "Detailed"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${view === k ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`cash-view-${k}`}>{l}</button>
            ))}
          </div>
          <CcyToggle value={ccy} onChange={setCcy} testid="cash-card-ccy" />
        </div>
      </div>
      <div className="px-5 py-4">
        <div className="font-heading text-3xl font-bold text-slate-900">{money(total, ccy)}</div>
        <div className="text-[11px] text-muted-foreground">Total Across {lines.length} Accounts{mgmt ? ` · 1 USD ≈ TZS ${Number(mgmt.usd_rate).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}</div>
        {view === "consolidated" ? (
          <ul className="mt-3 divide-y divide-border">
            {lines.map((l, i) => (
              <li key={i} className="flex items-center justify-between py-1.5 text-sm" data-testid={`cash-line-${i}`}>
                <span className="flex items-center gap-1.5 text-slate-600">{l.is_cash ? <Coins className="h-3.5 w-3.5 text-[#A87F31]" /> : <Building2 className="h-3.5 w-3.5 text-slate-400" />}{l.bank_name}<span className="rounded bg-slate-100 px-1.5 text-[10px]">{l.currency}</span></span>
                <span className="font-mono text-slate-900">{money(l.balance, l.currency)}</span>
              </li>
            ))}
            {!lines.length && <li className="py-8 text-center text-sm text-muted-foreground">No accounts.</li>}
          </ul>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-1.5 pr-2 font-medium">Account</th><th className="py-1.5 pr-2 font-medium">Cur</th><th className="py-1.5 text-right font-medium">Balance</th><th className="py-1.5 text-right font-medium">TZS Equiv</th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`cash-line-${i}`}>
                    <td className="py-1.5 pr-2 text-slate-900">{l.bank_name}</td>
                    <td className="py-1.5 pr-2"><span className="rounded-full bg-slate-100 px-2 text-xs">{l.currency}</span></td>
                    <td className="py-1.5 text-right font-mono">{money(l.balance, l.currency)}</td>
                    <td className="py-1.5 text-right font-mono text-slate-600">{l.tzs_equivalent == null ? "—" : money(l.tzs_equivalent, "TZS")}</td>
                  </tr>
                ))}
                {!lines.length && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No accounts.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const pctLabel = (x) => `${Math.round((x || 0) * 100)}%`;

function TechStat({ label, value, sub, Icon, tone }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid={`tech-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${tone}`} />
      </div>
      <div className="font-heading text-3xl font-bold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : <div className="mt-0.5 text-xs">&nbsp;</div>}
    </div>
  );
}

function TechnicianDashboard({ tech }) {
  return (
    <div className="space-y-6" data-testid="technician-dashboard">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <TechStat label="Opportunities Generated" value={tech?.generated ?? "—"} sub="Logged by you" Icon={Target} tone="text-[#1B4383]" />
        <TechStat label="Quotes Created" value={tech?.quoted ?? "—"} sub={tech ? `${pctLabel(tech.quote_rate)} of opportunities quoted` : ""} Icon={FileText} tone="text-[#A87F31]" />
        <TechStat label="Converted to Orders" value={tech?.won ?? "—"} sub={tech ? `${pctLabel(tech.conversion_rate)} conversion rate` : ""} Icon={Trophy} tone="text-[#527D63]" />
      </div>
      <div className="rounded-xl border border-border bg-white p-5">
        <h3 className="font-heading text-sm font-bold text-slate-800">Your Pipeline</h3>
        <div className="mt-3 grid grid-cols-3 gap-4 text-center">
          <div data-testid="tech-open"><div className="font-heading text-2xl font-bold text-slate-900">{tech?.open ?? "—"}</div><div className="text-xs text-muted-foreground">Open</div></div>
          <div data-testid="tech-won"><div className="font-heading text-2xl font-bold text-[#527D63]">{tech?.won ?? "—"}</div><div className="text-xs text-muted-foreground">Won</div></div>
          <div data-testid="tech-lost"><div className="font-heading text-2xl font-bold text-[#9D4848]">{tech?.lost ?? "—"}</div><div className="text-xs text-muted-foreground">Lost</div></div>
        </div>
        <button onClick={() => { window.location.href = "/opportunities"; }} className="mt-4 text-xs font-semibold text-[#1B4383] hover:underline" data-testid="tech-view-opps">View my opportunities →</button>
      </div>
      <ServiceCard s={tech?.service} />
    </div>
  );
}

const fmtDay = (d) => (d ? new Date(d.slice(0, 10)).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function ServiceCard({ s }) {
  const tiles = [
    ["open", "Open Jobs", s?.open, "text-slate-900"],
    ["completed", "Completed This Month", s?.completed_this_month, "text-[#527D63]"],
    ["overdue", "Overdue", s?.overdue, s?.overdue ? "text-[#9D4848]" : "text-slate-900"],
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="tech-service-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><Wrench className="h-4 w-4 text-[#1B4383]" />Compressor Servicing</div>
        <span className="text-[11px] text-muted-foreground" data-testid="tech-service-scope">{s?.scope === "mine" ? "Your work orders" : "All work orders"}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {tiles.map(([k, label, v, tone]) => (
          <div key={k} className="rounded-lg border border-border bg-slate-50/60 p-3 text-center" data-testid={`tech-service-${k}`}>
            <div className={`font-heading text-2xl font-bold ${tone}`}>{v ?? "—"}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
      <h4 className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Next Scheduled Services</h4>
      {s?.upcoming?.length ? (
        <ul className="mt-2 divide-y divide-border" data-testid="tech-service-upcoming">
          {s.upcoming.map((j) => (
            <li key={j.code} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`tech-upcoming-${j.code}`}>
              <div className="min-w-0">
                <span className="font-semibold text-slate-800">{j.code}</span>
                <span className="ml-2 text-slate-600">{j.asset_label || "—"}</span>
                <div className="text-xs text-muted-foreground">{[j.client_name, j.type, j.technician_name].filter(Boolean).join(" · ")}</div>
              </div>
              <span className="text-xs font-semibold text-[#1B4383]">{fmtDay(j.scheduled_date)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="tech-service-upcoming-empty">No upcoming services scheduled.</p>
      )}
      <button onClick={() => { window.location.href = "/service"; }} className="mt-3 text-xs font-semibold text-[#1B4383] hover:underline" data-testid="tech-view-service">Open Service Center →</button>
    </div>
  );
}

export default function Dashboard() {
  const [mgmt, setMgmt] = useState(null);
  const [ov, setOv] = useState(null);
  const [tech, setTech] = useState(null);
  const [revCcy, setRevCcy] = useState("TZS");
  const [profCcy, setProfCcy] = useState("TZS");
  const [cashKpiCcy, setCashKpiCcy] = useState("TZS");
  const [cc, setCc] = useState({});
  const [viewAs, setViewAs] = useState("management");
  const [confirmFinance, setConfirmFinance] = useState(false);
  const [quote, setQuote] = useState(dailyQuote());
  const [quoteAuthor, setQuoteAuthor] = useState("");
  const [quoteId, setQuoteId] = useState(null);
  const [quoteReactions, setQuoteReactions] = useState({ like: 0, love: 0 });
  const [quoteMine, setQuoteMine] = useState([]);
  const reactToday = async (type) => {
    if (!quoteId) return;
    try {
      const { data } = await api.post(`/motivational-quotes/${quoteId}/react`, { type });
      setQuoteReactions(data.reactions); setQuoteMine(data.my_reactions);
    } catch (e) { /* noop */ }
  };
  const { user } = useAuth();

  useEffect(() => {
    const canFin = user?.role === "admin" || ["management", "accounts"].includes(user?.department);
    if (canFin) api.get("/dashboard/management").then((r) => setMgmt(r.data)).catch(() => {});
    if (user?.department === "service" || user?.role === "admin") api.get("/dashboard/technician").then((r) => setTech(r.data)).catch(() => {});
    api.get("/dashboard/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/motivational-quotes/today").then((r) => { if (r.data?.text) setQuote(r.data.text); setQuoteAuthor(r.data?.author || ""); setQuoteId(r.data?.id || null); setQuoteReactions(r.data?.reactions || { like: 0, love: 0 }); setQuoteMine(r.data?.my_reactions || []); }).catch(() => {});
  }, [user]);

  const gc = (k) => cc[k] || "TZS";
  const sc = (k, v) => setCc((p) => ({ ...p, [k]: v }));
  const rate = ov?.usd_rate || mgmt?.usd_rate || 1;
  const conv = (tzs, ccy) => (ccy === "USD" ? Number(tzs || 0) / (rate || 1) : Number(tzs || 0));
  const pick = (obj, ccy) => (obj ? (ccy === "USD" ? obj.usd : obj.tzs) : null);

  const moneyCards = [
    { key: "revenue", label: "Total Revenue", icon: TrendingUp, tone: "text-[#1B4383]", ccy: revCcy, setCcy: setRevCcy, value: money(pick(mgmt?.revenue, revCcy), revCcy), sub: "Client Invoices Issued" },
    { key: "profit", label: "Gross Profit", icon: Coins, tone: "text-[#527D63]", ccy: profCcy, setCcy: setProfCcy, value: money(pick(mgmt?.profit, profCcy), profCcy), sub: mgmt ? `${(mgmt.profit.margin_pct * 100).toFixed(1)}% Margin` : "" },
    { key: "cash", label: "Cash in Bank", icon: Wallet, tone: "text-[#A87F31]", ccy: cashKpiCcy, setCcy: setCashKpiCcy, value: money(cashKpiCcy === "USD" ? mgmt?.cash?.total_usd : mgmt?.cash?.total_tzs, cashKpiCcy), sub: mgmt ? `${mgmt.cash.lines.length} Accounts` : "" },
  ];

  const sales = ov?.sales; const po = ov?.purchase_orders; const log = ov?.logistics; const fin = ov?.finance;
  const isAdmin = user?.role === "admin";
  const dept = isAdmin ? viewAs : (user?.department || "sales");
  const has = (...ds) => ds.includes(dept);
  const D = {
    revenue: has("management", "accounts"), profit: has("management", "accounts"),
    cash: has("management", "accounts"), winrate: has("management", "sales"),
    shipKpis: dept === "logistics", sales: has("management", "sales"),
    poPanel: has("management", "sales", "logistics"), logistics: has("management", "logistics"),
    financePanel: has("management"), ranks: has("management", "sales"),
  };

  const colsClass = (n) => (n <= 1 ? "" : n === 2 ? "md:grid-cols-2" : n === 3 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4");
  const kpiCount = (D.shipKpis ? 4 : 0) + moneyCards.filter((k) => D[k.key]).length + (D.winrate ? 1 : 0);
  const panelCount = [D.sales, D.poPanel, D.logistics, D.financePanel].filter(Boolean).length;
  const poData = po?.po_monthly || [];

  return (
    <AppLayout title="Dashboard">
      <UrgentRequestBanner />
      <FollowUpBanner />
      {/* Hero */}
      <div className="mb-6 flex flex-col gap-4 rounded-xl bg-[#12294B] px-6 py-6 text-white md:flex-row md:items-center md:justify-between">
        <div>
          <div className="font-heading text-2xl font-bold tracking-tight" data-testid="dashboard-greeting">Welcome Back, {user?.name || "there"}</div>
          <p className="mt-1 text-sm text-slate-300">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          {isAdmin && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="dashboard-viewas">
              <span className="text-xs text-slate-400">View As:</span>
              {["management", "sales", "logistics", "accounts", "service"].map((d) => (
                <button key={d} onClick={() => setViewAs(d)} className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize transition-colors ${viewAs === d ? "bg-white text-[#12294B]" : "bg-white/10 text-slate-200 hover:bg-white/20"}`} data-testid={`viewas-${d}`}>{d}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-start gap-2.5 border-t border-white/10 pt-4 md:max-w-sm md:border-l md:border-t-0 md:pl-5 md:pt-0" data-testid="dashboard-quote">
          <Quote className="mt-0.5 h-4 w-4 shrink-0 text-[#A87F31]" />
          <div>
            <p className="text-sm italic leading-snug text-slate-200">{quote}</p>
            {quoteAuthor && <p className="mt-1 text-xs font-medium text-[#A87F31]">— {quoteAuthor}</p>}
            {quoteId && (
              <div className="mt-2 flex items-center gap-2">
                <button onClick={() => reactToday("like")} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors ${quoteMine.includes("like") ? "bg-[#A87F31] text-white" : "bg-white/10 text-slate-200 hover:bg-white/20"}`} data-testid="dashboard-quote-like"><ThumbsUp className="h-3 w-3" />{quoteReactions.like || 0}</button>
                <button onClick={() => reactToday("love")} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors ${quoteMine.includes("love") ? "bg-[#C24E4E] text-white" : "bg-white/10 text-slate-200 hover:bg-white/20"}`} data-testid="dashboard-quote-love"><Heart className="h-3 w-3" />{quoteReactions.love || 0}</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {dept === "service" ? (
        <TechnicianDashboard tech={tech} />
      ) : (
      <>
      {/* KPI money cards */}
      <div className={`grid grid-cols-1 gap-4 ${colsClass(kpiCount)}`}>
        {D.shipKpis && [["Active Shipments", log?.active, Ship, "text-[#1B4383]"], ["At Risk (ETA Past)", log?.at_risk, AlertTriangle, "text-[#9D4848]"], ["Urgent (High Priority)", log?.urgent, AlertTriangle, "text-[#A87F31]"], ["Total Tracked", log?.total, ShoppingCart, "text-[#527D63]"]].map(([lbl, val, Icon, tone]) => (
          <div key={lbl} className="relative overflow-hidden rounded-xl border border-border bg-white p-5 shadow-sm" data-testid={`kpi-ship-${lbl}`}>
            <span className={`absolute inset-y-0 left-0 w-1 ${tone}`} style={{ backgroundColor: "currentColor" }} />
            <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{lbl}</span><Icon className={`h-4 w-4 ${tone}`} /></div>
            <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900">{val ?? "—"}</div>
          </div>
        ))}
        {moneyCards.filter((k) => D[k.key]).map((k) => (
          <div key={k.key} className="relative overflow-hidden rounded-xl border border-border bg-white p-5 shadow-sm" data-testid={`kpi-${k.key}`}>
            <span className={`absolute inset-y-0 left-0 w-1 ${k.tone}`} style={{ backgroundColor: "currentColor" }} />
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{k.label}</span>
              <div className="flex items-center gap-2">
                <CcyToggle value={k.ccy} onChange={k.setCcy} testid={`kpi-${k.key}-ccy`} />
                <k.icon className={`h-4 w-4 ${k.tone}`} />
              </div>
            </div>
            <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900">{k.value}</div>
            {k.sub && <div className="mt-1 text-xs text-muted-foreground">{k.sub}</div>}
          </div>
        ))}
        {D.winrate && (
        <div className="relative overflow-hidden rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="kpi-winrate">
          <span className="absolute inset-y-0 left-0 w-1 text-[#8A5A2B]" style={{ backgroundColor: "currentColor" }} />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Win Rate</span>
            <Trophy className="h-4 w-4 text-[#8A5A2B]" />
          </div>
          <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900">{ov?.pipeline ? `${(ov.pipeline.win_rate * 100).toFixed(0)}%` : "—"}</div>
          {ov?.pipeline && <div className="mt-1 text-xs text-muted-foreground">{ov.pipeline.won} Won · {ov.pipeline.open} Open</div>}
        </div>
        )}
      </div>

      {/* Operational summary cards */}
      {panelCount > 0 && (
      <div className={`mt-6 grid grid-cols-1 gap-4 ${colsClass(panelCount)}`}>
        {/* Sales */}
        <div className={`cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-[#1B4383]/40 ${D.sales ? "" : "hidden"}`} data-testid="summary-sales" onClick={() => { window.location.href = "/rfqs"; }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#1B4383]" /><h3 className="font-heading text-sm font-bold">Sales</h3></div>
            <span onClick={(e) => e.stopPropagation()}><CcyToggle value={gc("sales")} onChange={(v) => sc("sales", v)} testid="summary-sales-ccy" /></span>
          </div>
          <div className="mt-3 divide-y divide-border">
            <Row label="Pipeline (Open)" value={money(conv(sales?.pipeline_tzs, gc("sales")), gc("sales"))} tone="text-[#1B4383]" />
            <Row label="Open Opportunities" value={sales?.open_opps ?? "—"} />
            <Row label="Awaiting Decision" value={sales?.awaiting_decision ?? "—"} />
            <Row label={`Won · ${sales?.won_count ?? 0}`} value={money(conv(sales?.won_value_tzs, gc("sales")), gc("sales"))} tone="text-[#527D63]" />
          </div>
        </div>

        {/* Purchase Orders (per month) */}
        <div className={`cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-[#1B4383]/40 ${D.poPanel ? "" : "hidden"}`} data-testid="summary-po" onClick={() => { window.location.href = "/orders"; }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-[#8A5A2B]" /><h3 className="font-heading text-sm font-bold">Purchase Orders</h3></div>
            <span onClick={(e) => e.stopPropagation()}><CcyToggle value={gc("po")} onChange={(v) => sc("po", v)} testid="summary-po-ccy" /></span>
          </div>
          <div className="mt-3 rounded-lg bg-[#F6EFE2] px-3 py-2.5" data-testid="po-this-month">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A5A2B]">This Month · {ov?.month_label || ""}</div>
            <div className="mt-0.5 flex items-baseline justify-between">
              <span className="font-heading text-2xl font-bold text-slate-900">{po?.month_count ?? "—"}</span>
              <span className="font-mono text-sm font-semibold text-slate-700">{money(conv(po?.month_value_tzs, gc("po")), gc("po"))}</span>
            </div>
          </div>
          <div className="mt-2 divide-y divide-border">
            <Row label="Open Value" value={money(conv(po?.open_value_tzs, gc("po")), gc("po"))} />
            <Row label="Open / Completed" value={po ? `${po.open_count} / ${po.completed_count}` : "—"} />
            <Row label="Critical" value={po?.critical_count ?? "—"} tone={po?.critical_count ? "text-[#9D4848]" : "text-slate-900"} />
          </div>
        </div>

        {/* Logistics */}
        <div className={`cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-[#1B4383]/40 ${D.logistics ? "" : "hidden"}`} data-testid="summary-logistics" onClick={() => { window.location.href = "/logistics?section=shipments"; }}>
          <div className="flex items-center gap-2"><Ship className="h-4 w-4 text-[#1B4383]" /><h3 className="font-heading text-sm font-bold">Logistics</h3></div>
          <div className="mt-3 divide-y divide-border">
            <Row label="Active Shipments" value={log?.active ?? "—"} tone="text-[#1B4383]" />
            <Row label="At Risk (ETA Past)" value={log?.at_risk ?? "—"} tone={log?.at_risk ? "text-[#9D4848]" : "text-slate-900"} />
            <Row label="Urgent (High Priority)" value={log?.urgent ?? "—"} tone={log?.urgent ? "text-[#A87F31]" : "text-slate-900"} />
            <Row label="Total Tracked" value={log?.total ?? "—"} />
          </div>
        </div>

        {/* Finance */}
        <div className={`cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-[#1B4383]/40 ${D.financePanel ? "" : "hidden"}`} data-testid="summary-finance" onClick={() => setConfirmFinance(true)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-[#527D63]" /><h3 className="font-heading text-sm font-bold">Finance</h3></div>
            <span onClick={(e) => e.stopPropagation()}><CcyToggle value={gc("fin")} onChange={(v) => sc("fin", v)} testid="summary-finance-ccy" /></span>
          </div>
          <div className="mt-3 divide-y divide-border">
            <Row label={`Receivables · ${fin?.ar_count ?? 0} Inv`} value={money(conv(fin?.receivables_tzs, gc("fin")), gc("fin"))} tone="text-[#1B4383]" />
            <Row label={`Payables · ${fin?.ap_count ?? 0} Inv`} value={money(conv(fin?.payables_tzs, gc("fin")), gc("fin"))} tone="text-[#9D4848]" />
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">Live From Finance Hub</p>
        </div>
      </div>
      )}

      {/* Accounts: Finance + Cash in Bank side by side */}
      {dept === "accounts" && (
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-[#1B4383]/40" data-testid="summary-finance-acct" onClick={() => setConfirmFinance(true)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-[#527D63]" /><h3 className="font-heading text-sm font-bold">Finance</h3></div>
            <span onClick={(e) => e.stopPropagation()}><CcyToggle value={gc("fin")} onChange={(v) => sc("fin", v)} testid="summary-finance-acct-ccy" /></span>
          </div>
          <div className="mt-3 divide-y divide-border">
            <Row label={`Receivables · ${fin?.ar_count ?? 0} Inv`} value={money(conv(fin?.receivables_tzs, gc("fin")), gc("fin"))} tone="text-[#1B4383]" />
            <Row label={`Payables · ${fin?.ap_count ?? 0} Inv`} value={money(conv(fin?.payables_tzs, gc("fin")), gc("fin"))} tone="text-[#9D4848]" />
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">Live From Finance Hub</p>
        </div>
        <CashCard mgmt={mgmt} />
      </div>
      )}

      {/* Management: Purchase Orders trend + Cash in Bank */}
      {dept === "management" && (
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <POChart data={poData} rate={rate} />
        <CashCard mgmt={mgmt} />
      </div>
      )}

      {/* Sales / Logistics / Accounts: Purchase Orders trend full width */}
      {has("sales", "logistics", "accounts") && (
      <div className="mt-6">
        <POChart data={poData} rate={rate} />
      </div>
      )}

      {/* Ranked lists */}
      {D.ranks && (
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <RankCard icon={Users} title="Top Owners" testid="rank-owners" rows={ov?.top_owners || []} ccy={gc("owners")} onCcy={(v) => sc("owners", v)} conv={conv} />
        <RankCard icon={Building2} title="Top Clients" testid="rank-clients" rows={ov?.top_clients || []} ccy={gc("clients")} onCcy={(v) => sc("clients", v)} conv={conv} to={(r) => (r.id ? `/clients/${r.id}` : null)} />
        <RankCard icon={Factory} title="By Industry" testid="rank-industry" rows={ov?.by_industry || []} ccy={gc("inds")} onCcy={(v) => sc("inds", v)} conv={conv} to={(r) => (r.value ? `/industries/${r.value}` : null)} />
      </div>
      )}
      </>
      )}

      <AlertDialog open={confirmFinance} onOpenChange={setConfirmFinance}>
        <AlertDialogContent data-testid="finance-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Open the Finance Hub?</AlertDialogTitle>
            <AlertDialogDescription>This will take you to the Finance Hub. Are you sure you want to continue?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finance-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { window.location.href = "/finance"; }} data-testid="finance-confirm-go">Go to Finance Hub</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
